import { AudioSegment } from "../domain/types";
import { PayloadStore } from "../payload/PayloadStore";
import { buildWavHeader } from "./WavStreamWriter";

const toBlobParts = async (
  segments: AudioSegment[],
  payloadStore: PayloadStore
): Promise<BlobPart[]> => {
  const parts: BlobPart[] = [];

  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    const payload = await payloadStore.readSegment(segment.payloadKey);
    parts.push(payload);
  }

  return parts;
};

export class FinalArchiveBuilder {
  constructor(private readonly payloadStore: PayloadStore) {}

  async buildPcmBlob(segments: AudioSegment[]): Promise<Blob> {
    const parts = await toBlobParts(segments, this.payloadStore);
    return new Blob(parts, {
      type: "application/octet-stream",
    });
  }

  async buildWavBlob(segments: AudioSegment[], totalPcmBytes: number): Promise<Blob> {
    const parts = await toBlobParts(segments, this.payloadStore);
    const wavHeader = buildWavHeader(totalPcmBytes);
    const headerCopy = new Uint8Array(wavHeader.byteLength);
    headerCopy.set(wavHeader);
    const blobParts: BlobPart[] = [headerCopy.buffer];
    for (let index = 0; index < parts.length; index += 1) {
      blobParts.push(parts[index]);
    }

    return new Blob(blobParts, {
      type: "audio/wav",
    });
  }

  buildPcmStream(segments: AudioSegment[]): ReadableStream<Uint8Array> | null {
    if (typeof ReadableStream === "undefined") {
      return null;
    }

    let segmentIndex = 0;

    return new ReadableStream<Uint8Array>({
      pull: async (controller) => {
        if (segmentIndex >= segments.length) {
          controller.close();
          return;
        }

        const segment = segments[segmentIndex];
        const payload = await this.payloadStore.readSegment(segment.payloadKey);
        controller.enqueue(new Uint8Array(payload));
        segmentIndex += 1;
      },
    });
  }
}
